import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  Button,
  Divider,
  Stack,
  Box,
  Typography,
  Paper,
} from '@mui/material'
import FormInput from './FormInput'
import localization from '../utils/localization'
import ExtraForm from './ExtraForm'
import ContentUtils from '../utils/content'
import { BorderLinearProgress } from './ModalTotp'
import TotpUtils from '../utils/totp'
import copy from 'copy-to-clipboard'
import toast from 'react-hot-toast'

let globalTotpSecret = ''

const TotpForm = forwardRef((props, ref) => {
  const [title, setTitle] = useState(props.contentInfo.id ? props.contentInfo.title : '')
  const [titleErr, setTitleErr] = useState('')
  const [totpSecret, setTotpSecret] = useState(props.contentInfo.id ? props.contentInfo.content : '')
  const [totpSecretErr, setTotpSecretErr] = useState('')
  const [extra, setExtra] = useState(props.contentInfo.id ? props.contentInfo.extra : {})

  useEffect(() => {
    if (props.contentInfo.id) {
      setTitle(props.contentInfo.title)
      setTitleErr('')
      setTotpSecret(props.contentInfo.content)
      setTotpSecretErr('')
      setExtra(props.contentInfo.extra)
      globalTotpSecret = props.contentInfo.content
    }
  }, [props.contentInfo])

  const [totpToken, setTotpToken] = useState('')
  const [totpNumbers, setTotpNumbers] = useState([])
  const [progress, setProgress] = useState(0)

  function calcTotpToken() {
    let token = '000000'
    try {
      token = TotpUtils.generate(globalTotpSecret)
    } catch (e) { }
    
    setTotpToken(token)
    let modalTotpTime = new Date().getTime()
    const p = (modalTotpTime / 1000 % 30) * 100 / 30
    setProgress(p)
    
    const tokenStr = String(token).padStart(6, '0')
    setTotpNumbers(tokenStr.split(''))
  }

  useEffect(() => {
    calcTotpToken()
    let tickerID = setInterval(calcTotpToken, 1000)
    return () => clearInterval(tickerID)
  }, [])

  useImperativeHandle(ref, () => ({
    checkForm() {
      let hasErr = false
      if (title === '') {
        setTitleErr(localization.strings.required)
        hasErr = true
      }
      if (totpSecret === '') {
        setTotpSecretErr(localization.strings.required)
        hasErr = true
      }
      return hasErr
    },
    getContentData() {
      return {
        title: title,
        content: totpSecret,
        type: ContentUtils.getTotpType(),
        color: 'blue',
        account: '',
        extra: extra,
      }
    },
  }))

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <FormInput
          label={localization.strings.title}
          value={title}
          placeholder={localization.strings.inputTitleHint}
          error={titleErr}
          onChange={(event) => {
            setTitle(event.target.value)
            setTitleErr('')
          }}
        />
        <FormInput
          label={localization.strings.totpSecret}
          value={totpSecret}
          error={totpSecretErr}
          placeholder={localization.strings.inputTotpHint}
          onChange={(event) => {
            setTotpSecret(event.target.value)
            setTotpSecretErr('')
            globalTotpSecret = event.target.value
            calcTotpToken()
          }}
        />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '24px',
          border: '1px solid rgba(0,0,0,0.04)',
          backgroundColor: 'rgba(255,255,255,0.5)',
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <BorderLinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.05)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progress > 80 ? '#EF4444' : '#1E88E5',
              }
            }}
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
          {totpNumbers.map((num, index) => (
            <Box
              key={index}
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 56, sm: 64 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.03)',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: progress > 80 ? '#EF4444' : '#1E293B',
                  fontFamily: "'JetBrains Mono', 'Roboto Mono', monospace",
                  transition: 'color 0.3s ease',
                }}
              >
                {num}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            copy(`${totpToken}`)
            toast.success(localization.strings.copySuccess)
          }}
          sx={{
            borderRadius: '14px',
            py: 1.5,
            fontWeight: 700,
            backgroundColor: '#1E88E5',
            textTransform: 'none',
            boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)',
            '&:hover': { backgroundColor: '#1565C0' },
          }}
        >
          {localization.strings.clickCopy}
        </Button>
      </Paper>

      <Divider sx={{ opacity: 0.5 }} />
      <ExtraForm extra={extra} onChange={(e) => setExtra(e)} />
    </Stack>
  )
})

export default TotpForm
